/* ─────────────────────────────────────────────────────────────
   ANALYTICS URL SANITIZATION — shared by the client beacon AND the server route.

   WHY: /reset-password carries a credential-equivalent token in its query string
   (`?token=…`). Two leak vectors put it into analytics_events:
     1. `path`     — was built as `${pathname}?${query}`
     2. `referrer` — document.referrer is the FULL previous URL, so navigating AWAY
                     from the reset page leaks the token even if `path` is clean.
   Either one would persist a live reset token into a table that /admin/analytics
   renders as "Top pages".

   RULE: ALLOWLIST, never a denylist. A denylist of "sensitive" names only protects the
   params someone thought of; the next token param nobody anticipated would sail through.
   Anything not explicitly named here is DROPPED — unknown params fail CLOSED.

   Hash fragments are dropped entirely (implicit-flow tokens live there too).

   Used on BOTH sides on purpose: the client must not send it, and the server must not
   store it even if something POSTs to /api/analytics/events directly.
───────────────────────────────────────────────────────────── */

/** Query params that are safe to keep — product-analytics signal, never credentials. */
export const ANALYTICS_PARAM_ALLOWLIST: ReadonlySet<string> = new Set([
  'q', 'page', 'sort', 'filter', 'tab',
])

/** Keep only allowlisted params, preserving their order. Returns '' or '?a=1&b=2'. */
function filterSearch(params: URLSearchParams): string {
  const out = new URLSearchParams()
  params.forEach((value, key) => {
    if (ANALYTICS_PARAM_ALLOWLIST.has(key.toLowerCase())) out.append(key, value)
  })
  const s = out.toString()
  return s ? `?${s}` : ''
}

/** Everything before the first `?` or `#` — the fail-closed fallback when parsing fails. */
function bareBeforeQuery(s: string): string | null {
  const cut = s.split(/[?#]/)[0]
  return cut || null
}

/** Path prefixes whose NEXT segment is a secret token (credential-equivalent) and must be redacted
 *  from any stored/exported path or referrer — e.g. /verify/<one-time-token>, /reset-password/<token>. */
const SENSITIVE_PATH_PREFIXES = ['/verify/', '/reset-password/']
function redactSensitivePath(pathname: string): string {
  for (const p of SENSITIVE_PATH_PREFIXES) {
    if (pathname.startsWith(p) && pathname.length > p.length) return `${p}[redacted]`
  }
  return pathname
}

/**
 * Sanitize a PATH for storage: pathname + allowlisted params only.
 * Accepts a relative path ('/x?token=1') or an absolute URL (origin is dropped).
 * '/reset-password?token=abc' → '/reset-password'
 * '/search?q=masjid&token=abc' → '/search?q=masjid'
 */
export function sanitizeAnalyticsPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const s = raw.trim()
  if (!s) return null
  try {
    // The base only resolves RELATIVE inputs; an absolute input keeps its own path.
    const u = new URL(s, 'https://analytics.invalid')
    return `${redactSensitivePath(u.pathname)}${filterSearch(u.searchParams)}`
  } catch {
    const bare = bareBeforeQuery(s)
    return bare ? redactSensitivePath(bare) : bare
  }
}

/**
 * Sanitize a REFERRER for storage: origin + pathname + allowlisted params only.
 * 'https://site/reset-password?token=abc' → 'https://site/reset-password'
 */
export function sanitizeAnalyticsReferrer(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const s = raw.trim()
  if (!s) return null
  try {
    const u = new URL(s) // a referrer is absolute
    return `${u.origin}${redactSensitivePath(u.pathname)}${filterSearch(u.searchParams)}`
  } catch {
    const bare = bareBeforeQuery(s)
    return bare ? redactSensitivePath(bare) : bare
  }
}
